import type { Question, QuestionChangeRoot, QuestionPath } from '@/types';
import type { Answers, SlideValidation } from '@/redux/slider';
import Condition from '@/utils/condition';
import Converter from '@/utils/converter';
import TypeGuard from '@/utils/type-guard';

function removeIsRoot(question: Question): Question {
  const { isRoot, changeRoot, ...rest } = question;
  return isRoot ? rest : question;
}

function addIsRootUpdatePath(question: Question, changeRoot: QuestionChangeRoot, rootQuestion: Question): Question {
  const { isRoot, path, ...rest } = question;
  if (question.key === changeRoot.root) {
    return {
      ...rest,
      isRoot: true,
      message: question.message || rootQuestion.message,
      path: [
        {
          condition: () => true,
          value: changeRoot.path || [changeRoot.root],
        },
      ],
    };
  }
  return question;
}

export function changeRoot(questions: Question[]): {
  rootChanged: null | { key: string; value: string };
  questions: Question[];
} {
  const questionWithChangeRoot = questions.find((question) => question.isRoot && question.changeRoot);
  if (questionWithChangeRoot) {
    const changeRoot = Condition.getFirst(questionWithChangeRoot.changeRoot || []);

    return changeRoot
      ? {
          rootChanged: {
            key: questionWithChangeRoot.key,
            value: changeRoot.value,
          },
          questions: questions
            .map((question) => removeIsRoot(question))
            .map((question) => addIsRootUpdatePath(question, changeRoot, questionWithChangeRoot)),
        }
      : { rootChanged: null, questions };
  } else {
    return {
      rootChanged: null,
      questions,
    };
  }
}

export function initializeValidation(questions: Question[]): Record<string, SlideValidation> {
  return questions.reduce((validation, question) => {
    return {
      ...validation,
      [question.key]: { required: !!question.validate?.required, dirty: false, messages: [] },
    };
  }, {});
}

export function filter(questions: Question[]): Question[] {
  const rootWithPath = questions.find((question) => question.isRoot && question.path);
  if (rootWithPath) {
    const questionPath = Condition.getFirst<QuestionPath, QuestionPath>(rootWithPath.path || [], []);
    const questionKeys = questionPath.filter((key) => questions.find((question) => question.key === key));
    return Array.from(new Set([rootWithPath.key, ...questionKeys, 'formular']))
      .map((key) => questions.find((question) => question.key === key))
      .filter((question) => !!question);
  } else {
    return questions;
  }
}

export function getDefaultValue(question?: Question): string | null {
  if (!question) return null;
  switch (question.type) {
    case 'text':
    case 'text-large':
    case 'textarea':
    case 'dropdown':
    case 'datefield':
    case 'paragraph':
      const { defaultValue } = question;
      return TypeGuard.isNotUndefined(defaultValue) ? defaultValue : null;
    case 'multiple':
    case 'multiple-text':
      const { choices } = question;
      return TypeGuard.isNotUndefined(choices)
        ? Converter.Answer.FromArrayToMultiple(choices.filter((c) => c.isDefault).map((c) => c.value)) || null
        : null;
    case 'submission':
      return null;
    default:
      return null;
  }
}

type AnswerModifierFn = (questions: Question[], answers: Answers) => Answers;
type AnswerModifierMainFn = (questions: Question[], answers: Answers, modifier: AnswerModifierFn[]) => Answers;

export const finalValues: AnswerModifierFn = (questions, answers) => {
  return Object.entries(answers).reduce((answers, [key, answer]) => {
    const finalValue = questions.find((question) => question.key === key)?.finalValue;
    return {
      ...answers,
      [key]: Condition.isEmpty(answer) && Condition.isNotUndefined(finalValue) ? finalValue : answer,
    };
  }, {});
};

export const dateMonthStyle: AnswerModifierFn = (questions, answers) => {
  return Object.entries(answers).reduce((answers, [key, answer]) => {
    const question = questions.find((question) => question.key === key && question.calendarPrecision === 'month');
    return {
      ...answers,
      [key]: question ? Converter.DateSting.ToMonthStyle(answer) : answer,
    };
  }, {});
};

export const applyModifiers: AnswerModifierMainFn = (questions, answers, modifiers) => {
  return modifiers.reduce((result, modifier) => modifier(questions, result), answers);
};
